import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBasket, User, Search, Menu, X, Instagram } from "lucide-react";
import { LOGO_WORDMARK, LOGO_MOUNTAIN } from "../lib/api";
import { useAuth, useCart } from "../lib/ctx";

const NAV = [
  { to: "/shop", label: "Shop All" },
  { to: "/honey", label: "Honey" },
  { to: "/supplements", label: "Supplements" },
  { to: "/coming-soon", label: "Coming Soon" },
  { to: "/consultation", label: "Consultation" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function TikTok(props) { return (<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}><path d="M19 8.2a6.2 6.2 0 0 1-3.7-1.2v7.6a5.6 5.6 0 1 1-5.6-5.6c.3 0 .6 0 .9.1v2.7a2.9 2.9 0 1 0 2 2.8V2h2.7A4.2 4.2 0 0 0 19 6.1z"/></svg>); }

export function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = React.useState(false);
  return (
    <header className="sticky top-0 z-50 oh-glass" data-testid="site-header">
      <div className="oh-announcement text-center py-2 px-4" data-testid="promo-banner">
        <span>★ 10% OFF SELECTED PRODUCTS · Automatic at checkout · Free UK delivery over £40 ★</span>
      </div>
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 flex items-center gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
          <img src={LOGO_MOUNTAIN} alt="Organic Healing" className="h-11 w-auto hidden sm:block" />
          <img src={LOGO_WORDMARK} alt="Organic Healing" className="h-7 w-auto sm:hidden" />
        </Link>
        <nav className="hidden lg:flex items-center gap-6 ml-6 flex-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,"-")}`}
              className={({ isActive }) => `text-sm tracking-wide link-hover ${isActive ? "text-lime-400" : "text-white/85"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-4 ml-auto">
          <a href="https://www.instagram.com/organichealinguk" target="_blank" rel="noreferrer" className="link-hover hidden sm:block" data-testid="ig-header"><Instagram size={18} /></a>
          <a href="https://www.tiktok.com/@organichealinguk" target="_blank" rel="noreferrer" className="link-hover hidden sm:block" data-testid="tt-header"><TikTok /></a>
          {user && ["owner","manager","stock_controller","order_processor","read_only"].includes(user.role) ? (
            <div className="hidden md:flex items-center gap-3">
              <Link to="/admin" data-testid="account-link" className="text-sm link-hover flex items-center gap-1"><User size={16}/>{user.name?.split(" ")[0]}</Link>
              <button onClick={logout} data-testid="logout-btn" className="text-xs text-white/60 link-hover">Sign out</button>
            </div>
          ) : (
            <Link to="/track" data-testid="track-link" className="hidden md:inline text-sm link-hover">Track Order</Link>
          )}
          <Link to="/basket" data-testid="basket-link" className="relative flex items-center gap-2 oh-btn-ghost px-3 py-2 rounded-full text-sm">
            <ShoppingBasket size={16}/>
            <span className="hidden sm:inline">Basket</span>
            {count > 0 && <span className="oh-badge oh-badge-lime !text-[10px] !py-0.5 !px-2" data-testid="basket-count">{count}</span>}
          </Link>
          <button onClick={() => setOpen((v) => !v)} className="lg:hidden text-white" data-testid="mobile-menu-toggle">{open ? <X/> : <Menu/>}</button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[var(--oh-border)] px-4 py-4 space-y-3 bg-[var(--oh-surface)]">
          {NAV.map((n) => <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block text-white/90" data-testid={`mnav-${n.label.toLowerCase()}`}>{n.label}</Link>)}
          <Link to="/track" onClick={() => setOpen(false)} className="block text-lime-400">Track My Order</Link>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="mt-24 border-t border-[var(--oh-border)] bg-[var(--oh-surface)]">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="space-y-4">
          <img src={LOGO_MOUNTAIN} alt="Organic Healing" className="h-14 w-auto"/>
          <p className="text-sm text-white/70 max-w-xs">Nature's finest, delivered to you. Carefully selected honey, natural supplements and traditional wellness products.</p>
          <div className="flex gap-3">
            <a href="https://www.instagram.com/organichealinguk" target="_blank" rel="noreferrer" data-testid="ig-footer" className="link-hover"><Instagram size={20}/></a>
            <a href="https://www.tiktok.com/@organichealinguk" target="_blank" rel="noreferrer" data-testid="tt-footer" className="link-hover"><TikTok /></a>
          </div>
        </div>
        <div>
          <h4 className="oh-badge oh-badge-lime inline-block mb-4">Shop</h4>
          <ul className="space-y-2 text-sm text-white/75">
            <li><Link to="/shop" className="link-hover">Shop All</Link></li>
            <li><Link to="/honey" className="link-hover">Honey</Link></li>
            <li><Link to="/supplements" className="link-hover">Supplements</Link></li>
            <li><Link to="/coming-soon" className="link-hover">Coming Soon</Link></li>
            <li><Link to="/offers" className="link-hover">Offers</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="oh-badge oh-badge-turq inline-block mb-4">Company</h4>
          <ul className="space-y-2 text-sm text-white/75">
            <li><Link to="/about" className="link-hover">About</Link></li>
            <li><Link to="/delivery" className="link-hover">Delivery & Collection</Link></li>
            <li><Link to="/reviews" className="link-hover">Google Reviews</Link></li>
            <li><Link to="/contact" className="link-hover">Contact & Location</Link></li>
            <li><Link to="/track" className="link-hover">Order Tracking</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="oh-badge oh-badge-gold inline-block mb-4">Legal</h4>
          <ul className="space-y-2 text-sm text-white/75">
            <li><Link to="/faq" className="link-hover">FAQ</Link></li>
            <li><Link to="/privacy" className="link-hover">Privacy Policy</Link></li>
            <li><Link to="/terms" className="link-hover">Terms & Conditions</Link></li>
            <li><Link to="/returns" className="link-hover">Returns & Refunds</Link></li>
            <li><Link to="/delivery-policy" className="link-hover">Delivery Policy</Link></li>
            <li><Link to="/cookies" className="link-hover">Cookie Policy</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[var(--oh-border)] py-5 px-4 text-center text-xs text-white/50 font-mono tracking-widest space-y-1">
        <div>Unit 13D, 47 Whitmore Rd · Small Heath · Birmingham B10 0NR · +44 7877 855305</div>
        <div>© {new Date().getFullYear()} ORGANIC HEALING · THE NATURAL PATH · Registered in the UK</div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  if (isAdmin) return <>{children}</>;
  return (
    <div className="oh-topo min-h-screen">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
